/**
 * Template definitions for NEVCO Pro Video simulation
 *
 * Currently only box score templates (Baseball + Softball) are active.
 * Sport-specific scoreboard/lower-third/bug/general templates will be
 * added when NewBlue delivers SVG assets for import.
 *
 * Layer coordinate system: 1280×720 design space
 */
import type { TemplateDefinition, TemplateField, TemplateLayer } from '@graphicsim/shared-types';

// ============================================================
// Baseball/Softball Box Score — FISD-style inning-by-inning grid
// Reference: assets/titles/FISD Baseball_Softball Scoreboard Template.pdf
// ============================================================

const BOX_SCORE_BG = '#2a3a5c';
const INNINGS = 7;
const SCORE_BOX_SIZE = 58;

function boxScoreTemplate(
  id: string,
  sport: string,
  sportLabel: string,
  homeColorDefault: string,
  visitorColorDefault: string,
): TemplateDefinition {
  // Layout constants
  const FX = 30;       // frame x
  const FY = 320;      // frame y
  const FW = 1220;     // frame width
  const FH = 290;      // frame height

  // Column grid — innings start after the team info section
  const GRID_X = 490;  // where inning columns begin
  const COL_W = 68;    // width per column
  const GAP = 30;      // gap between inning 7 and R column
  const innX = (i: number) => GRID_X + i * COL_W;
  const sumX = (i: number) => GRID_X + INNINGS * COL_W + GAP + i * COL_W;

  // Row positions
  const HDR_Y = FY + 8;
  const HDR_H = 42;
  const ROW1_Y = FY + 55;   // home row
  const ROW2_Y = FY + 165;  // visitor row
  const ROW_H = 100;
  const BOX_OX = (COL_W - SCORE_BOX_SIZE) / 2;  // center box in column
  const BOX_OY = (ROW_H - SCORE_BOX_SIZE) / 2;

  // --- Header labels ---
  const headerLayers: TemplateLayer[] = [];
  for (let i = 0; i < INNINGS; i++) {
    headerLayers.push({
      id: `hdr-i${i + 1}`, type: 'text',
      x: innX(i), y: HDR_Y, width: COL_W, height: HDR_H,
      content: `${i + 1}`, fontSize: 30, fontWeight: 'bold', textColor: '#ffffff',
      textAlign: 'center', fontFamily: 'Arial, sans-serif',
    });
  }
  ['R', 'H', 'E'].forEach((lbl, i) => {
    headerLayers.push({
      id: `hdr-${lbl.toLowerCase()}`, type: 'text',
      x: sumX(i), y: HDR_Y, width: COL_W, height: HDR_H,
      content: lbl, fontSize: 30, fontWeight: 'bold', textColor: '#ffffff',
      textAlign: 'center', fontFamily: 'Arial, sans-serif',
    });
  });

  // --- Score box builder (box + text for one cell) ---
  function scoreCell(prefix: string, fieldId: string, col: number, rowY: number): TemplateLayer[] {
    const bx = col + BOX_OX;
    const by = rowY + BOX_OY;
    return [
      {
        id: `${prefix}-bg`, type: 'rect',
        x: bx, y: by, width: SCORE_BOX_SIZE, height: SCORE_BOX_SIZE,
        fill: '#000000', borderRadius: 3,
      },
      {
        id: `${prefix}-txt`, type: 'text',
        x: bx, y: by, width: SCORE_BOX_SIZE, height: SCORE_BOX_SIZE,
        content: `{${fieldId}}`, fontSize: 26, fontWeight: 'bold', textColor: '#ffffff',
        textAlign: 'center', fontFamily: 'Arial, sans-serif',
      },
    ];
  }

  // --- Home inning cells ---
  const homeCells: TemplateLayer[] = [];
  for (let i = 0; i < INNINGS; i++) {
    homeCells.push(...scoreCell(`h-i${i + 1}`, `inn${i + 1}Home`, innX(i), ROW1_Y));
  }
  homeCells.push(...scoreCell('h-r', 'runsHome', sumX(0), ROW1_Y));
  homeCells.push(...scoreCell('h-h', 'hitsHome', sumX(1), ROW1_Y));
  homeCells.push(...scoreCell('h-e', 'errorsHome', sumX(2), ROW1_Y));

  // --- Visitor inning cells ---
  const visCells: TemplateLayer[] = [];
  for (let i = 0; i < INNINGS; i++) {
    visCells.push(...scoreCell(`v-i${i + 1}`, `inn${i + 1}Vis`, innX(i), ROW2_Y));
  }
  visCells.push(...scoreCell('v-r', 'runsVis', sumX(0), ROW2_Y));
  visCells.push(...scoreCell('v-h', 'hitsVis', sumX(1), ROW2_Y));
  visCells.push(...scoreCell('v-e', 'errorsVis', sumX(2), ROW2_Y));

  // --- Team info layers ---
  const TEAM_X = FX + 20;
  const LOGO_SIZE = 90;

  function teamInfoLayers(
    prefix: string,
    rowY: number,
    cityField: string,
    mascotField: string,
    recordField: string,
    colorField: string,
  ): TemplateLayer[] {
    return [
      // Logo box
      {
        id: `${prefix}-logo`, type: 'rect',
        x: TEAM_X, y: rowY + 5, width: LOGO_SIZE, height: LOGO_SIZE,
        fill: `{${colorField}}`, borderRadius: 6,
      },
      // Gradient shine on logo
      {
        id: `${prefix}-shine`, type: 'rect',
        x: TEAM_X, y: rowY + 5, width: LOGO_SIZE, height: LOGO_SIZE,
        fill: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, transparent 60%)',
        borderRadius: 6,
      },
      // City name (small, above mascot)
      {
        id: `${prefix}-city`, type: 'text',
        x: TEAM_X + LOGO_SIZE + 15, y: rowY + 8, width: 300, height: 26,
        content: `{${cityField}}`, fontSize: 17, textColor: '#cccccc',
        textAlign: 'left', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase',
      },
      // Mascot name (large bold)
      {
        id: `${prefix}-mascot`, type: 'text',
        x: TEAM_X + LOGO_SIZE + 15, y: rowY + 30, width: 260, height: 45,
        content: `{${mascotField}}`, fontSize: 34, fontWeight: 'bold', textColor: '#ffffff',
        textAlign: 'left', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase',
      },
      // Record
      {
        id: `${prefix}-record`, type: 'text',
        x: TEAM_X + LOGO_SIZE + 280, y: rowY + 42, width: 120, height: 28,
        content: `{${recordField}}`, fontSize: 16, textColor: '#999999',
        textAlign: 'left', fontFamily: 'Arial, sans-serif',
      },
    ];
  }

  // --- Fields ---
  const fields: TemplateField[] = [
    // Home team info
    { id: 'homeCity', label: 'Home City', type: 'text', defaultValue: 'HOME CITY', group: 'Home Team' },
    { id: 'homeMascot', label: 'Home Mascot', type: 'text', defaultValue: 'TEAM', group: 'Home Team' },
    { id: 'homeRecord', label: 'Home Record', type: 'text', defaultValue: '(0-0, 0-0)', group: 'Home Team' },
    { id: 'homeColor', label: 'Home Color', type: 'color', defaultValue: homeColorDefault, group: 'Home Team' },
    // Visitor team info
    { id: 'visitorCity', label: 'Visitor City', type: 'text', defaultValue: 'VISITOR CITY', group: 'Visitor Team' },
    { id: 'visitorMascot', label: 'Visitor Mascot', type: 'text', defaultValue: 'TEAM', group: 'Visitor Team' },
    { id: 'visitorRecord', label: 'Visitor Record', type: 'text', defaultValue: '(0-0, 0-0)', group: 'Visitor Team' },
    { id: 'visitorColor', label: 'Visitor Color', type: 'color', defaultValue: visitorColorDefault, group: 'Visitor Team' },
    // Home inning scores
    ...Array.from({ length: INNINGS }, (_, i) => ({
      id: `inn${i + 1}Home`, label: `Inn ${i + 1} Home`, type: 'text' as const, defaultValue: '', group: 'Home Innings',
    })),
    // Visitor inning scores
    ...Array.from({ length: INNINGS }, (_, i) => ({
      id: `inn${i + 1}Vis`, label: `Inn ${i + 1} Visitor`, type: 'text' as const, defaultValue: '', group: 'Visitor Innings',
    })),
    // Home R/H/E
    { id: 'runsHome', label: 'Runs Home', type: 'text', defaultValue: '', group: 'Home Summary' },
    { id: 'hitsHome', label: 'Hits Home', type: 'text', defaultValue: '', group: 'Home Summary' },
    { id: 'errorsHome', label: 'Errors Home', type: 'text', defaultValue: '', group: 'Home Summary' },
    // Visitor R/H/E
    { id: 'runsVis', label: 'Runs Visitor', type: 'text', defaultValue: '', group: 'Visitor Summary' },
    { id: 'hitsVis', label: 'Hits Visitor', type: 'text', defaultValue: '', group: 'Visitor Summary' },
    { id: 'errorsVis', label: 'Errors Visitor', type: 'text', defaultValue: '', group: 'Visitor Summary' },
    // Background
    { id: 'bgColor', label: 'Background Color', type: 'color', defaultValue: BOX_SCORE_BG, group: 'Colors' },
  ];

  // --- Assemble layers ---
  return {
    id,
    name: `${sportLabel} Box Score`,
    category: 'scoreboard',
    sport,
    fields,
    layers: [
      // Outer frame
      {
        id: 'frame', type: 'rect',
        x: FX, y: FY, width: FW, height: FH,
        fill: '{bgColor}', borderRadius: 10,
        boxShadow: '0 4px 20px rgba(0,0,0,0.6)',
      },
      // Header row separator line
      {
        id: 'hdr-line', type: 'rect',
        x: FX + 10, y: FY + HDR_H + 10, width: FW - 20, height: 2,
        fill: 'rgba(255,255,255,0.15)',
      },
      // Row divider between home and visitor
      {
        id: 'row-div', type: 'rect',
        x: FX + 10, y: ROW2_Y - 5, width: FW - 20, height: 1,
        fill: 'rgba(255,255,255,0.08)',
      },
      // Header labels
      ...headerLayers,
      // Home team info
      ...teamInfoLayers('home', ROW1_Y, 'homeCity', 'homeMascot', 'homeRecord', 'homeColor'),
      // Home score cells
      ...homeCells,
      // Visitor team info
      ...teamInfoLayers('vis', ROW2_Y, 'visitorCity', 'visitorMascot', 'visitorRecord', 'visitorColor'),
      // Visitor score cells
      ...visCells,
    ],
    width: 1280,
    height: 720,
    duration: 0,
    contentBounds: { x: FX, y: FY, w: FW, h: FH },
  };
}

// ============================================================
// Football Bottom Scoreboard — SVG-based template
// Reference: assets/titles/Football Bottom Scoreboard.svg
// Properties: assets/screenshots/Properties Tab Bottom Score.JPG
// ============================================================

const FOOTBALL_BOTTOM_SVG = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 1920 1080">
<defs>
<style>
.fbs-score{font-size:185px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;}
.fbs-clock{font-size:85px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;}
.fbs-info{font-size:60px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;}
.fbs-label{font-size:50px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;}
.fbs-team{font-size:80px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;}
.fbs-period{font-size:85px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;}
.fbs-dark{fill:#1a1a1a;}
</style>
<linearGradient id="fbs-grad-time" x1="960.78" y1="858.43" x2="960.78" y2="740.88" gradientUnits="userSpaceOnUse">
<stop offset="0" stop-color="#000"/>
<stop offset="1" stop-color="#666"/>
</linearGradient>
<linearGradient id="fbs-grad-period" x1="1060.78" y1="1049.32" x2="1060.78" y2="937.32" gradientUnits="userSpaceOnUse">
<stop offset="0" stop-color="#000"/>
<stop offset="1" stop-color="#666"/>
</linearGradient>
</defs>
<g id="BASE">
<rect width="1920" height="1080" fill="none"/>
</g>
<g id="TIME_PERIOD">
<rect id="INFO_BG" fill="#0a1628" x="755" y="725" width="465" height="340" rx="14" ry="14" opacity="0.88"/>
<rect id="INFO_BORDER" fill="none" stroke="#3a7ecf" stroke-width="1.5" x="755" y="725" width="465" height="340" rx="14" ry="14" opacity="0.6"/>
<rect id="TIME_BORDER" data-fill="gradient1" fill="url(#fbs-grad-time)" x="792.44" y="740.88" width="336.67" height="117.56"/>
<rect id="TIME_MAIN" class="fbs-dark" x="806.78" y="752.21" width="306.44" height="94"/>
<rect id="PERIOD_BORDER" data-fill="gradient1" fill="url(#fbs-grad-period)" x="1004.78" y="937.32" width="112" height="112"/>
<rect id="PERIOD_MAIN" class="fbs-dark" x="1015.78" y="948.32" width="90" height="90"/>
</g>
<g id="HOME">
<rect id="HOME_SCORE_BOX" data-fill="homeColor" fill="#39b54a" x="429.67" y="760.5" width="275" height="275"/>
<rect id="HOME_LOGO_BOX" data-fill="homeColor" fill="#39b54a" x="133" y="760.5" width="275" height="275" opacity="0.7"/>
<rect x="175" y="800" width="190" height="190" fill="rgba(255,255,255,0.08)" rx="6"/>
<image data-image="homeLogo" x="148" y="775" width="245" height="245" href="/assets/Lesson Media/Team Logo 1.png" preserveAspectRatio="xMidYMid meet"/>
</g>
<g id="VISITOR">
<rect id="VISITOR_SCORE_BOX" data-fill="visitorColor" fill="#00a99d" x="1221.39" y="760.5" width="275" height="275"/>
<rect id="VISITOR_LOGO_BOX" data-fill="visitorColor" fill="#00a99d" x="1511.49" y="760.5" width="275" height="275" opacity="0.7"/>
<rect x="1553" y="800" width="190" height="190" fill="rgba(255,255,255,0.08)" rx="6"/>
<image data-image="visitorLogo" x="1526" y="775" width="245" height="245" href="/assets/Lesson Media/Team Logo 2.png" preserveAspectRatio="xMidYMid meet"/>
</g>
<g id="STATIC_TEXT">
<text class="fbs-label" transform="translate(1040 920.79)">ON:</text>
</g>
<g id="EDIT_TEXT">
<text class="fbs-clock" data-field="clock1" data-max-width="290" transform="translate(960 829.46)">00:00</text>
<text class="fbs-score" data-field="homeScore" data-max-width="240" transform="translate(567.17 961.14)">00</text>
<text class="fbs-score" data-field="visitorScore" data-max-width="240" transform="translate(1358.89 961.14)">00</text>
<text class="fbs-period" data-field="period" data-max-width="75" transform="translate(1060.78 1026.43)">1</text>
<text class="fbs-info" data-field="downDistance" data-max-width="300" transform="translate(874 925.97)">1ST &amp; 10</text>
<text class="fbs-info" data-field="ballOn" data-max-width="110" transform="translate(1161 925.97)">00</text>
<text class="fbs-info" data-field="periodLabel" data-max-width="270" transform="translate(894 1026.43)">PERIOD</text>
<text class="fbs-team" data-field="homeTeam" data-max-width="270" transform="translate(106.73 898) rotate(-90)">HOME</text>
<text class="fbs-team" data-field="visitorTeam" data-max-width="270" transform="translate(1815.6 898) rotate(90)">VISITOR</text>
</g>
</svg>`;

function footballBottomScoreboard(): TemplateDefinition {
  return {
    id: 'fb-bottom-scoreboard',
    name: 'Football Bottom Scoreboard',
    category: 'scoreboard',
    sport: 'football',
    svgContent: FOOTBALL_BOTTOM_SVG,
    width: 1920,
    height: 1080,
    contentBounds: { x: 0, y: 720, w: 1920, h: 360 },
    layers: [],
    duration: 0,
    fields: [
      { id: 'homeTeam', label: 'Home Team', type: 'text', defaultValue: 'home' },
      { id: 'visitorTeam', label: 'Visitor Team', type: 'text', defaultValue: 'visitor' },
      { id: 'homeScore', label: 'Home Score', type: 'text', defaultValue: '00' },
      { id: 'visitorScore', label: 'Visitor Score', type: 'text', defaultValue: '00' },
      { id: 'period', label: 'Period', type: 'text', defaultValue: '1' },
      { id: 'down', label: 'Down', type: 'dropdown', defaultValue: '1', options: ['1', '2', '3', '4'] },
      { id: 'distance', label: 'Distance', type: 'text', defaultValue: '10' },
      { id: 'ballOn', label: 'Ball On', type: 'text', defaultValue: '00' },
      { id: 'periodLabel', label: 'Period Label', type: 'text', defaultValue: 'PERIOD' },
      { id: 'homeLogo', label: 'Home Logo', type: 'image', defaultValue: '/assets/Lesson Media/Team Logo 1.png' },
      { id: 'visitorLogo', label: 'Visitor Logo', type: 'image', defaultValue: '/assets/Lesson Media/Team Logo 2.png' },
      { id: 'gradient1', label: 'Gradient 1', type: 'gradient', defaultValue: '#000066' },
      { id: 'bgColor', label: 'Background Color', type: 'color', defaultValue: '#1a1a1a' },
      { id: 'color1', label: 'Color 1', type: 'color', defaultValue: '#0066ff' },
      { id: 'homeColor', label: 'Home Color', type: 'color', defaultValue: '#39b54a' },
      { id: 'visitorColor', label: 'Visitor Color', type: 'color', defaultValue: '#00a99d' },
      { id: 'clock1', label: 'Clock 1', type: 'text', defaultValue: '00:00' },
    ],
  };
}

// ============================================================
// Football Halftime Stats — SVG-based template (loaded from file)
// Reference: assets/titles/Football Halftime Stats.svg
// ============================================================

function footballHalftimeStats(): TemplateDefinition {
  return {
    id: 'fb-halftime-stats',
    name: 'Football Halftime Stats',
    category: 'fullscreen',
    sport: 'football',
    svgUrl: '/assets/titles/Football Halftime Stats.svg',
    width: 1920,
    height: 892,
    layers: [],
    duration: 0,
    fields: [
      // Home team stats (right column)
      { id: 'homeTotalYards', label: 'Home Total Yards', type: 'text', defaultValue: '000', group: 'Home Stats' },
      { id: 'homePassingYards', label: 'Home Passing Yards', type: 'text', defaultValue: '000', group: 'Home Stats' },
      { id: 'homeRushYards', label: 'Home Rush Yards', type: 'text', defaultValue: '000', group: 'Home Stats' },
      { id: 'homeTurnovers', label: 'Home Turnovers', type: 'text', defaultValue: '000', group: 'Home Stats' },
      { id: 'homePenalties', label: 'Home Penalties-Yds', type: 'text', defaultValue: '000', group: 'Home Stats' },
      // Away team stats (left column)
      { id: 'awayTotalYards', label: 'Away Total Yards', type: 'text', defaultValue: '000', group: 'Away Stats' },
      { id: 'awayPassingYards', label: 'Away Passing Yards', type: 'text', defaultValue: '000', group: 'Away Stats' },
      { id: 'awayRushYards', label: 'Away Rush Yards', type: 'text', defaultValue: '000', group: 'Away Stats' },
      { id: 'awayTurnovers', label: 'Away Turnovers', type: 'text', defaultValue: '000', group: 'Away Stats' },
      { id: 'awayPenalties', label: 'Away Penalties-Yds', type: 'text', defaultValue: '000', group: 'Away Stats' },
    ],
  };
}

// ============================================================
// Football Scoreboard (compact bottom band) — SVG-based template (inline).
// Historically named "Football Lower Third"; the id fb-lower-third is kept for
// compatibility with saved projects and lesson selectors.
// Reference: assets/titles/Virtual Scoring Templates/Football/
//   Football_1280x240.svg (base shapes)
//   Football_Lower 1_3 w_background_(1280x240).png (visual reference)
// ============================================================

const FOOTBALL_LOWER_THIRD_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1280 720">
<defs>
<style>
.flt-score{font-size:56px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;dominant-baseline:central;}
.flt-clock{font-size:80px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;dominant-baseline:central;}
.flt-team{font-size:38px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;dominant-baseline:central;}
.flt-qtr{font-size:22px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;dominant-baseline:central;}
.flt-down{font-size:32px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;dominant-baseline:central;}
.flt-dist{font-size:32px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:400;text-anchor:start;dominant-baseline:central;}
</style>
</defs>
<g id="BASE">
<rect width="1280" height="720" fill="none"/>
</g>
<g id="BACKGROUND">
<g data-toggle="showBackground"><rect id="BACKGROUND_BASE" data-fill="bgColor" x="1" y="472.61" width="1280" height="240" style="fill:#02024c;"/></g>
<rect id="INFO_BG" x="497" y="522" width="286" height="158" rx="14" ry="14" style="fill:#0a1628; opacity:0.88;"/>
<rect id="INFO_BORDER" x="497" y="522" width="286" height="158" rx="14" ry="14" style="fill:none; stroke:#3a7ecf; stroke-width:1.5; opacity:0.6;"/>
<rect x="520.94" y="632.11" width="84.11" height="42.56" rx="21.28" ry="21.28" style="fill:#020222; stroke:#fff; stroke-miterlimit:10; stroke-width:.5px;"/>
</g>
<g id="HOME_SCORE">
<rect id="HOME_BASE" x="62.67" y="548.33" width="428" height="126.06" rx="38.56" ry="38.56" style="fill:#020322; stroke:#b3b3b3; stroke-miterlimit:10; stroke-width:.5px;"/>
<rect id="HOME_NAME" data-fill="homeColor" x="62.96" y="548.36" width="427.71" height="81.5" rx="39.73" ry="39.73" style="fill:#0083d4;"/>
<rect id="HOME_PT" x="332.61" y="546.56" width="158.06" height="81.56" rx="40.78" ry="40.78" style="fill:#000035; stroke:#0083d4; stroke-miterlimit:10; stroke-width:.5px;"/>
<rect id="HOME_TO_1" x="101.17" y="650.33" width="42.94" height="5.86" style="fill:#808190;"/>
<rect id="HOME_TO_2" x="158.83" y="650.33" width="42.94" height="5.86" style="fill:#808190;"/>
<rect id="HOME_TO_3" x="216.5" y="650.33" width="42.94" height="5.86" style="fill:#808190;"/>
</g>
<g id="AWAY_SCORE">
<rect id="AWAY_BASE" x="789.27" y="548.99" width="428" height="126.06" rx="38.56" ry="38.56" transform="translate(2006.54 1224.05) rotate(-180)" style="fill:#020322; stroke:#b3b3b3; stroke-miterlimit:10; stroke-width:.5px;"/>
<rect id="AWAY_NAME" data-fill="awayColor" x="789.27" y="549.02" width="427.71" height="81.5" rx="39.73" ry="39.73" transform="translate(2006.25 1179.54) rotate(-180)" style="fill:#0083d4;"/>
<rect id="AWAY_PT" x="789.27" y="547.22" width="158.06" height="81.56" rx="40.78" ry="40.78" transform="translate(1736.6 1175.99) rotate(180)" style="fill:#000035; stroke:#0083d4; stroke-miterlimit:10; stroke-width:.5px;"/>
<rect id="AWAY_TO_1" x="1135.83" y="650.99" width="42.94" height="5.86" transform="translate(2314.6 1307.85) rotate(-180)" style="fill:#808190;"/>
<rect id="AWAY_TO_2" x="1078.16" y="650.99" width="42.94" height="5.86" transform="translate(2199.26 1307.85) rotate(-180)" style="fill:#808190;"/>
<rect id="AWAY_TO_3" x="1020.49" y="650.99" width="42.94" height="5.86" transform="translate(2083.93 1307.85) rotate(-180)" style="fill:#808190;"/>
</g>
<g id="HOME_POSSESSION" data-toggle="homePossession">
<image x="95" y="574" width="28" height="28" href="/assets/icons/football.png" preserveAspectRatio="xMidYMid meet"/>
</g>
<g id="AWAY_POSSESSION" data-toggle="awayPossession">
<image x="1157" y="574" width="28" height="28" href="/assets/icons/football.png" preserveAspectRatio="xMidYMid meet"/>
</g>
<g id="EDIT_TEXT">
<text class="flt-team" data-field="homeTeam" data-max-width="175" transform="translate(210 589)">HOME</text>
<text class="flt-score" data-field="homeScore" data-max-width="120" transform="translate(412 588)">0</text>
<text class="flt-clock" data-field="clock" data-max-width="260" transform="translate(640 570)">00:00</text>
<text class="flt-score" data-field="awayScore" data-max-width="120" transform="translate(868 588)">0</text>
<text class="flt-team" data-field="awayTeam" data-max-width="175" transform="translate(1070 589)">AWAY</text>
<text class="flt-qtr" data-field="quarter" data-max-width="60" transform="translate(563 653)">QTR 1</text>
<text class="flt-down" data-field="down" data-max-width="60" transform="translate(640 653)">1st</text>
<text class="flt-dist" data-field="distance" data-max-width="80" transform="translate(680 653)">&amp; 10</text>
</g>
</svg>`;

function footballLowerThird(): TemplateDefinition {
  return {
    id: 'fb-lower-third',
    name: 'Football Scoreboard',
    category: 'scoreboard',
    sport: 'football',
    svgContent: FOOTBALL_LOWER_THIRD_SVG,
    width: 1280,
    height: 720,
    contentBounds: { x: 0, y: 472, w: 1280, h: 248 },
    layers: [],
    duration: 0,
    fields: [
      { id: 'homeTeam', label: 'Home Team', type: 'text', defaultValue: 'HOME', group: 'Home' },
      { id: 'homeScore', label: 'Home Score', type: 'text', defaultValue: '0', group: 'Home' },
      { id: 'homeColor', label: 'Home Color', type: 'color', defaultValue: '#0083d4', group: 'Home' },
      { id: 'homePossession', label: 'Home Possession', type: 'toggle', defaultValue: 'true', group: 'Home' },
      { id: 'awayTeam', label: 'Away Team', type: 'text', defaultValue: 'AWAY', group: 'Away' },
      { id: 'awayScore', label: 'Away Score', type: 'text', defaultValue: '0', group: 'Away' },
      { id: 'awayColor', label: 'Away Color', type: 'color', defaultValue: '#0083d4', group: 'Away' },
      { id: 'awayPossession', label: 'Away Possession', type: 'toggle', defaultValue: 'false', group: 'Away' },
      { id: 'clock', label: 'Clock', type: 'text', defaultValue: '00:00', group: 'Game' },
      { id: 'quarter', label: 'Quarter', type: 'text', defaultValue: 'QTR 1', group: 'Game' },
      { id: 'down', label: 'Down', type: 'text', defaultValue: '1st', group: 'Game' },
      { id: 'distance', label: 'Distance', type: 'text', defaultValue: '& 10', group: 'Game' },
      { id: 'showBackground', label: 'Show Background', type: 'toggle', defaultValue: 'false', group: 'Colors' },
      { id: 'bgColor', label: 'Background Color', type: 'color', defaultValue: '#02024c', group: 'Colors' },
    ],
  };
}

const FOOTBALL_MATCHUP_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1280 720">
<defs>
<style>
.fmu-label{font-size:24px;fill:#cdd6e4;font-family:'Segoe UI',sans-serif;font-weight:600;letter-spacing:6px;text-anchor:middle;dominant-baseline:central;}
.fmu-team{font-size:46px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;dominant-baseline:central;}
.fmu-record{font-size:24px;fill:#9fb0c6;font-family:'Segoe UI',sans-serif;font-weight:400;text-anchor:middle;dominant-baseline:central;}
.fmu-vs{font-size:34px;fill:#fff;font-family:'Segoe UI',sans-serif;font-weight:700;text-anchor:middle;dominant-baseline:central;}
.fmu-detail{font-size:24px;fill:#cdd6e4;font-family:'Segoe UI',sans-serif;font-weight:400;text-anchor:middle;dominant-baseline:central;}
</style>
</defs>
<g id="BASE">
<rect width="1280" height="720" fill="none"/>
</g>
<g id="BACKGROUND">
<g data-toggle="showBackground"><rect id="BACKGROUND_BASE" data-fill="bgColor" x="0" y="472" width="1280" height="248" style="fill:#02024c;"/></g>
<rect id="LABEL_PLATE" x="440" y="486" width="400" height="38" rx="19" ry="19" style="fill:#0a1628; opacity:0.88;"/>
<rect id="DETAIL_PLATE" x="470" y="674" width="340" height="36" rx="18" ry="18" style="fill:#0a1628; opacity:0.88;"/>
</g>
<g id="HOME_BLOCK">
<rect id="HOME_PLATE" x="110" y="534" width="470" height="128" rx="16" ry="16" style="fill:#0a1628; opacity:0.92;"/>
<rect id="HOME_STRIPE" data-fill="homeColor" x="110" y="534" width="14" height="128" rx="7" ry="7" style="fill:#0083d4;"/>
<rect id="HOME_RULE" data-fill="homeColor" x="250" y="612" width="190" height="4" rx="2" ry="2" style="fill:#0083d4;"/>
</g>
<g id="AWAY_BLOCK">
<rect id="AWAY_PLATE" x="700" y="534" width="470" height="128" rx="16" ry="16" style="fill:#0a1628; opacity:0.92;"/>
<rect id="AWAY_STRIPE" data-fill="awayColor" x="1156" y="534" width="14" height="128" rx="7" ry="7" style="fill:#c8102e;"/>
<rect id="AWAY_RULE" data-fill="awayColor" x="840" y="612" width="190" height="4" rx="2" ry="2" style="fill:#c8102e;"/>
</g>
<g id="VS_BADGE">
<circle cx="640" cy="598" r="42" style="fill:#020322; stroke:#fff; stroke-width:2; opacity:0.95;"/>
<text class="fmu-vs" transform="translate(640 599)">VS</text>
</g>
<g id="EDIT_TEXT">
<text class="fmu-label" data-field="matchupLabel" data-max-width="360" transform="translate(640 505)">TONIGHT'S MATCHUP</text>
<text class="fmu-team" data-field="homeTeam" data-max-width="380" transform="translate(345 578)">HOME</text>
<text class="fmu-record" data-field="homeRecord" data-max-width="300" transform="translate(345 638)">0-0</text>
<text class="fmu-team" data-field="awayTeam" data-max-width="380" transform="translate(935 578)">AWAY</text>
<text class="fmu-record" data-field="awayRecord" data-max-width="300" transform="translate(935 638)">0-0</text>
<text class="fmu-detail" data-field="detail" data-max-width="700" transform="translate(640 692)">KICKOFF 7:00 PM</text>
</g>
</svg>`;

function footballMatchup(): TemplateDefinition {
  return {
    id: 'fb-matchup',
    name: 'Football Matchup',
    category: 'lower-third',
    sport: 'football',
    svgContent: FOOTBALL_MATCHUP_SVG,
    width: 1280,
    height: 720,
    contentBounds: { x: 0, y: 472, w: 1280, h: 248 },
    layers: [],
    duration: 0,
    fields: [
      { id: 'matchupLabel', label: 'Matchup Label', type: 'text', defaultValue: "TONIGHT'S MATCHUP", group: 'Game' },
      { id: 'detail', label: 'Detail Line', type: 'text', defaultValue: 'KICKOFF 7:00 PM', group: 'Game' },
      { id: 'homeTeam', label: 'Home Team', type: 'text', defaultValue: 'HOME', group: 'Home' },
      { id: 'homeRecord', label: 'Home Record', type: 'text', defaultValue: '0-0', group: 'Home' },
      { id: 'homeColor', label: 'Home Color', type: 'color', defaultValue: '#0083d4', group: 'Home' },
      { id: 'awayTeam', label: 'Away Team', type: 'text', defaultValue: 'AWAY', group: 'Away' },
      { id: 'awayRecord', label: 'Away Record', type: 'text', defaultValue: '0-0', group: 'Away' },
      { id: 'awayColor', label: 'Away Color', type: 'color', defaultValue: '#c8102e', group: 'Away' },
      { id: 'showBackground', label: 'Show Background', type: 'toggle', defaultValue: 'false', group: 'Colors' },
      { id: 'bgColor', label: 'Background Color', type: 'color', defaultValue: '#02024c', group: 'Colors' },
    ],
  };
}

// ============================================================
// Assemble all templates
// ============================================================

export const templates: TemplateDefinition[] = [
  boxScoreTemplate('bb-boxscore', 'baseball', 'Baseball', '#c8102e', '#003da5'),
  boxScoreTemplate('sb-boxscore', 'softball', 'Softball', '#d4001a', '#1e4d8c'),
  footballBottomScoreboard(),
  footballHalftimeStats(),
  footballLowerThird(),
  footballMatchup(),
];
